import { useState, useCallback, useRef, useEffect } from 'react';

export function useDebounceHandler(callback, milliseconds) {
  const timerRef = useRef(null);
  return useCallback(
    function(...args) {
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        if (typeof callback === 'function') {
          callback(...args);
        }
      }, milliseconds);
    },
    [callback, milliseconds]
  );
}

export default function useForm({ initialValues, rules }) {
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (initialValues) setFormData(initialValues);
  }, [initialValues]);

  const setFieldListValue = fieldList =>
    setFormData({
      ...formData,
      ...fieldList,
    });

  const setFieldErrorListValue = fieldErrorList =>
    setErrors({
      ...errors,
      ...fieldErrorList,
    });

  const onFieldChange = (field, value) => {
    setFormData({
      ...formData,
      [field]: value,
    });

    debounceValidateField(field, value);
  };

  const validate = (rule, value) => {
    if (rule.required && !value) {
      return Promise.reject(rule.message);
    }
    if (rule.validator && value) {
      return rule.validator(formData, value);
    }
    return Promise.resolve();
  };

  const validateField = async (field, value) => {
    const validations = rules[field];

    if (!validations) return;

    try {
      for (let i = 0; i < validations.length; i++) {
        const validation = validations[i];
        await validate(validation, value);
      }

      if (errors[field]) setErrors(state => ({ ...state, [field]: '' }));
      return Promise.resolve();
    } catch (error) {
      setErrors(state => ({ ...state, [field]: error }));
      return Promise.reject(error);
    }
  };

  const debounceValidateField = useDebounceHandler(validateField, 1000);

  const validateFields = (fieldList = Object.keys(formData)) => {
    if (!rules) return;

    let promises = [];

    for (let i = 0; i < fieldList.length; i++) {
      const field = fieldList[i];
      const value = formData[field] || '';
      promises.push(validateField(field, value));
    }

    return Promise.all(promises);
  };

  return {
    formData,
    onFieldChange,
    errors,
    validateFields,
    setFieldListValue,
    setFieldErrorListValue,
  };
}



// WEBPACK FOOTER //
// ./app/hooks/useForm.js